// DealerRequestBlock — Dealer billing (2026-08-31).
//
// 🔒 THE CONTRACT (spec §9): this renders BELOW the existing card content and
// NOTHING above it moves. `rateKind !== 'dealer_billing'` returns null, so a
// request with no dealer is byte-identical to today. 67 live requests share
// this card; 38 approved and 22 rejected are already history.
//
// 🔒 This component renders NO buttons. Approve / Reject stay exactly where they
// are, owned by the page. Adding a button here is how an action got deleted the
// first time this was attempted.
//
// 🔒 The field is `dealerSpread`, never `dealerMargin` — that name is on the
// COST_FIELDS deny list. The visible LABEL is "Dealer margin". Both deliberate.
import React from 'react';

export interface DealerRequestFields {
  rateKind?: string | null;
  dealer?: { name: string; gstin?: string | null } | null;
  billToPartyType?: string | null;
  customerDiscountPct?: number | null;
  customerPays?: number | null;
  dealerRatePct?: number | null;
  revenueExpected?: number | null;
  dealerSpread?: number | null;
}

const inr = (v: number | null | undefined) =>
  v == null ? '—' : `₹${Math.round(v).toLocaleString('en-IN')}`;

const billToLabel = (t: string | null | undefined) =>
  t === 'dealer' ? 'Dealer' : t === 'customer' ? 'Customer' : '—';

export function DealerRequestBlock({ request }: { request: DealerRequestFields }) {
  if (request?.rateKind !== 'dealer_billing') return null;
  const negative = (request.dealerSpread ?? 0) < 0;

  return (
    <div
      data-testid="dealer-request-block"
      className={`mt-3 border-t pt-3 ${negative ? 'border-rose-300' : 'border-indigo-200'}`}
    >
      <div
        className={`text-xs font-semibold uppercase tracking-wide ${
          negative ? 'text-rose-700' : 'text-indigo-700'
        }`}
      >
        {negative ? 'Revenue below the customer price' : 'Through dealer'}
        {' — '}
        {request.dealer?.name ?? 'Unknown dealer'}
        {request.dealer?.gstin ? ` · GSTIN ${request.dealer.gstin}` : ''}
      </div>
      <dl className="mt-2 grid grid-cols-2 gap-x-6 gap-y-1 text-sm sm:grid-cols-4">
        <div>
          <dt className="text-slate-500">Bill to</dt>
          <dd>{billToLabel(request.billToPartyType)}</dd>
        </div>
        <div>
          <dt className="text-slate-500">Customer discount</dt>
          <dd>{request.customerDiscountPct ?? '—'}%</dd>
        </div>
        <div>
          <dt className="text-slate-500">Customer pays</dt>
          <dd>{inr(request.customerPays)}</dd>
        </div>
        <div>
          <dt className="text-slate-500">Dealer rate asked</dt>
          <dd>{request.dealerRatePct ?? '—'}%</dd>
        </div>
        <div>
          <dt className="text-slate-500">Revenue expected</dt>
          <dd>{inr(request.revenueExpected)}</dd>
        </div>
        <div>
          <dt className="text-slate-500">Dealer margin</dt>
          <dd className={negative ? 'font-bold text-rose-700' : 'font-bold text-indigo-700'}>
            {inr(request.dealerSpread)}
          </dd>
        </div>
      </dl>
      {negative && (
        <p className="mt-2 text-sm text-rose-700">
          We would bill the dealer more than the customer pays. Usually this means the
          customer discount was approved without the dealer rate in view.
        </p>
      )}
    </div>
  );
}
